import React from "react";
import { Navigate, Route } from "react-router-dom";

import { useProfile } from "../Hooks/UserHooks";
import usePermission from "../Hooks/usePermission";

/**
 * AuthProtected component that handles both authentication and authorization.
 *
 * Props:
 * - children: The component to render if access is granted
 * - permission: Single permission required (e.g., 'asset:read')
 * - permissions: Array of permissions (used with operator)
 * - operator: 'any' (default) or 'all' for permission combination
 * - adminOnly: If true, only admins can access
 * - superAdminOnly: If true, only super admins can access
 * - fallbackPath: Where to redirect if permission denied (default: '/dashboard')
 */
const AuthProtected = ({
  children,
  permission,
  permissions,
  operator = 'any',
  adminOnly = false,
  superAdminOnly = false,
  fallbackPath = '/dashboard',
  ...props
}) => {
  const { userProfile, loading } = useProfile();
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    isAdmin,
  } = usePermission();

  // Check authentication first
  if (!userProfile && loading) {
    return (
      <Navigate to={{ pathname: "/login", state: { from: props.location } }} />
    );
  }

  // If no permission requirements, just render children (authenticated only)
  const hasPermissionRequirements = permission || permissions || adminOnly || superAdminOnly;
  if (!hasPermissionRequirements) {
    return <>{children}</>;
  }

  // Check authorization
  const checkAccess = () => {
    // Super admin bypass (unless checking specifically for super admin)
    if (!superAdminOnly && isSuperAdmin) {
      return true;
    }

    // Super admin only check
    if (superAdminOnly) {
      return isSuperAdmin;
    }

    // Admin only check
    if (adminOnly) {
      return isAdmin;
    }

    // Single permission check
    if (permission) {
      return hasPermission(permission);
    }

    // Multiple permissions check
    if (permissions && permissions.length > 0) {
      return operator === 'all'
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
    }

    return false;
  };

  if (checkAccess()) {
    return <>{children}</>;
  }

  // Permission denied - redirect to fallback
  return <Navigate to={{ pathname: fallbackPath }} replace />;
};

const AccessRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => { return ( <> <Component {...props} /> </> );
      }}
    />
  );
};

export { AuthProtected, AccessRoute };



